import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../../supabaseClient';

const Loader = ({ texto = 'Cargando...' }) => (
  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', gap: '20px' }}>
    <div style={{ position: 'relative', width: '56px', height: '56px' }}>
      <div style={{ position: 'absolute', inset: 0, borderRadius: '50%', border: '3px solid #0f172a' }} />
      <div style={{ position: 'absolute', inset: 0, borderRadius: '50%', border: '3px solid transparent', borderTopColor: '#00f2ff', animation: 'spin 0.9s linear infinite' }} />
      <div style={{ position: 'absolute', inset: '8px', borderRadius: '50%', border: '2px solid transparent', borderTopColor: '#a855f7', animation: 'spin 1.4s linear infinite reverse' }} />
    </div>
    <p style={{ color: '#475569', fontSize: '0.78rem', letterSpacing: '2px', textTransform: 'uppercase', margin: 0 }}>{texto}</p>
    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
  </div>
);

const TorneoPage = ({ session, tipo, esAdmin }) => {
  const navigate = useNavigate();

  const abandonarTorneo = async (e, torneoId) => {
    e.stopPropagation();
    if (!window.confirm('¿Seguro que querés abandonar este torneo?')) return;
    // Salir del torneo
    await supabase.from('torneo_participantes').delete()
      .eq('torneo_id', torneoId).eq('user_id', session.user.id);
    // Si quedó vacío → disolver completamente
    const { count } = await supabase.from('torneo_participantes')
      .select('id', { count: 'exact', head: true }).eq('torneo_id', torneoId);
    if (count === 0) {
      await supabase.from('comodines_usuario').delete().eq('torneo_id', torneoId);
      await supabase.from('torneo_puntos').delete().eq('torneo_id', torneoId);
      await supabase.from('casillero_config').delete().eq('torneo_id', torneoId);
      await supabase.from('torneo_fechas').delete().eq('torneo_id', torneoId);
      await supabase.from('torneos').delete().eq('id', torneoId);
    }
    setTorneos(prev => prev.filter(t => t.id !== torneoId));
  };

  const borrarTorneo = async (e, torneoId) => {
    e.stopPropagation();
    if (!window.confirm('¿Seguro que querés eliminar este torneo? No se puede deshacer.')) return;
    // Borrar en cascada
    await supabase.from('comodines_usuario').delete().eq('torneo_id', torneoId);
    await supabase.from('torneo_puntos').delete().eq('torneo_id', torneoId);
    await supabase.from('casillero_config').delete().eq('torneo_id', torneoId);
    await supabase.from('torneo_fechas').delete().eq('torneo_id', torneoId);
    await supabase.from('torneo_participantes').delete().eq('torneo_id', torneoId);
    await supabase.from('torneos').delete().eq('id', torneoId);
    setTorneos(prev => prev.filter(t => t.id !== torneoId));
  };
  const [torneos, setTorneos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [vista, setVista] = useState('lista'); // 'lista' | 'crear' | 'unirse'
  const [nombreNuevo, setNombreNuevo] = useState('');
  const [codigoIngreso, setCodigoIngreso] = useState('');
  const [error, setError] = useState('');
  const [creando, setCreando] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const [resultadosBusqueda, setResultadosBusqueda] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const [uniendose, setUniendose] = useState(null);
  const [torneoPublico, setTorneoPublico] = useState(false);
  const [codigo, setCodigo] = useState('');

  const tipoLabel = tipo === 'tactico' ? 'Táctico' : 'Libre';
  const tipoColor = tipo === 'tactico' ? '#00f2ff' : '#a855f7';

  useEffect(() => { fetchTorneos(); setVista('lista'); }, [tipo]);

  const fetchTorneos = async () => {
    setLoading(true);
    const { data } = await supabase
      .from('torneo_participantes')
      .select(`torneo:torneos(id, nombre, tipo, codigo_invitacion, creador_id, created_at, clan_id, clan:clanes(tag))`)
      .eq('user_id', session.user.id);

    const misTorneos = (data || [])
      .map(d => d.torneo)
      .filter(t => t && t.tipo === tipo);

    // Traer conteo de participantes para mostrar botón Salir/Borrar correctamente
    const torneosConConteo = await Promise.all(misTorneos.map(async (t) => {
      const { count } = await supabase
        .from('torneo_participantes')
        .select('id', { count: 'exact', head: true })
        .eq('torneo_id', t.id);
      return { ...t, participantes: count || 0 };
    }));

    setTorneos(torneosConConteo);
    setLoading(false);
  };

  const buscarTorneos = async (texto) => {
    setBusqueda(texto);
    setBuscando(true);
    try {
      let query = supabase.from('torneos')
        .select('id, nombre, tipo, creador_id, created_at')
        .eq('tipo', tipo)
        .eq('publico', true)
        .order('created_at', { ascending: false })
        .limit(20);
      if (texto.trim()) query = query.ilike('nombre', `%${texto}%`);
      const { data: torneoData } = await query;

      if (!torneoData?.length) { setResultadosBusqueda([]); setBuscando(false); return; }

      // Contar participantes y obtener nombre del creador
      const results = await Promise.all(torneoData.map(async (t) => {
        const [{ count }, { data: creador }] = await Promise.all([
          supabase.from('torneo_participantes').select('*', { count: 'exact', head: true }).eq('torneo_id', t.id),
          supabase.from('perfiles').select('nickname').eq('id', t.creador_id).maybeSingle(),
        ]);
        // Verificar si ya está en el torneo
        const { count: yaEsta } = await supabase
          .from('torneo_participantes').select('*', { count: 'exact', head: true })
          .eq('torneo_id', t.id).eq('user_id', session.user.id);
        return { ...t, participantes: count || 0, creador_nick: creador?.nickname || '?', yaEsta: yaEsta > 0 };
      }));
      setResultadosBusqueda(results);
    } catch(e) { console.error(e); }
    finally { setBuscando(false); }
  };

  const unirseDirecto = async (torneoId) => {
    setUniendose(torneoId);
    try {
      const { data: perfil } = await supabase.from('perfiles').select('nickname').eq('id', session.user.id).single();
      await supabase.from('torneo_participantes').insert({
        torneo_id: torneoId,
        user_id: session.user.id,
        nombre_usuario: perfil?.nickname || session.user.email.split('@')[0],
      });
      navigate(`/torneo/${torneoId}`);
    } catch(e) { console.error(e); }
    finally { setUniendose(null); }
  };

  const crearTorneo = async () => {
    if (!nombreNuevo.trim()) { setError('Ingresá un nombre'); return; }
    setError('');

    // Validar equipo armado antes de crear (solo táctico)
    if (tipo === 'tactico') {
      const { data: ventana } = await supabase
        .from('ventana_transferencias').select('numero_fecha').eq('activa', true)
        .order('numero_fecha', { ascending: false }).limit(1).maybeSingle();
      const jornada = ventana?.numero_fecha ?? 1;
      const { data: drafts } = await supabase
        .from('drafts').select('confirmado, equipo')
        .eq('user_id', session.user.id).eq('jornada', jornada);
      const draft = drafts?.[0];
      const cantJugadores = draft?.equipo ? Object.values(draft.equipo).filter(Boolean).length : 0;
      if (!draft?.confirmado || cantJugadores < 11) {
        setError(' Tenés que armar y confirmar tu equipo táctico antes de crear un torneo. Andá a Mi Equipo → Táctico.');
        return;
      }
    }

    setCreando(true);
    try {
      const { data: perfil } = await supabase
        .from('perfiles').select('nickname').eq('id', session.user.id).single();
      const nickNameCrear = perfil?.nickname || session.user.email.split('@')[0];

      const { data: torneo, error: err } = await supabase
        .from('torneos')
        .insert({ nombre: nombreNuevo.trim(), tipo, creador_id: session.user.id, publico: torneoPublico })
        .select().single();
      if (err) throw err;

      // ⚠️ NO BORRAR: crear fechas automáticamente (5 fechas, fecha 1 activa)
      const fechas = Array.from({ length: 5 }, (_, i) => ({
        torneo_id: torneo.id, numero_fecha: i + 1, cerrada: false, activa: i === 0
      }));
      await supabase.from('torneo_fechas').insert(fechas);

      // ⚠️ NO BORRAR: crear casilleros por defecto (diseño fijo para todos los torneos)
      const CASILLEROS_DEFAULT = [
        { casillero: 2,  tipo: 'comodin_aleatorio',   descripcion: 'La suerte está de tu lado... o no 🎲',    emoji: '🃏', casilleros_efecto: 0, monto_efecto: 0 },
        { casillero: 4,  tipo: 'comodin_aleatorio',   descripcion: 'Abrís el sobre y... sorpresa',             emoji: '📦', casilleros_efecto: 0, monto_efecto: 0 },
        { casillero: 6,  tipo: 'comodin_fijo',        descripcion: '+$1M ¡El mercado te sonríe!',              emoji: '💵', casilleros_efecto: 0, monto_efecto: 0,        comodin_tipo_id: 'dinero_extra' },
        { casillero: 7,  tipo: 'trampa_combo',        descripcion: 'Mala decisión táctica. Retrocedés 1 y perdés $1M.', emoji: '📉', casilleros_efecto: 1, monto_efecto: 1000000 },
        { casillero: 8,  tipo: 'trampa_retroceso',    descripcion: 'Lesión de último momento. Retrocedés 2.', emoji: '🩹', casilleros_efecto: 2, monto_efecto: 0 },
        { casillero: 10, tipo: 'comodin_aleatorio',   descripcion: 'El tablero te tira algo random',           emoji: '🎰', casilleros_efecto: 0, monto_efecto: 0 },
        { casillero: 11, tipo: 'trampa_combo',        descripcion: 'Fichaje frustrado. Retrocedés 2 y perdés $2M.', emoji: '😤', casilleros_efecto: 2, monto_efecto: 2000000 },
        { casillero: 12, tipo: 'comodin_fijo',        descripcion: 'Espiás el equipo rival antes de confirmar', emoji: '👁️', casilleros_efecto: 0, monto_efecto: 0,       comodin_tipo_id: 'ver_rival' },
        { casillero: 13, tipo: 'trampa_retroceso',    descripcion: 'VAR en tu contra. Retrocedés 3 casilleros.', emoji: '📺', casilleros_efecto: 3, monto_efecto: 0 },
        { casillero: 14, tipo: 'comodin_fijo',        descripcion: '+$2.5M Fichaje de lujo desbloqueado',      emoji: '💰', casilleros_efecto: 0, monto_efecto: 0,        comodin_tipo_id: 'dinero_extra_plus' },
        { casillero: 15, tipo: 'trampa_combo',        descripcion: 'Retrocedés 2 Y perdés $2M. Mala fecha.',   emoji: '💸', casilleros_efecto: 2, monto_efecto: 2000000 },
        { casillero: 16, tipo: 'bonus_avance',        descripcion: '¡Racha ganadora! Avanzás 2 más.',          emoji: '🔥', casilleros_efecto: 2, monto_efecto: 0 },
        { casillero: 17, tipo: 'comodin_aleatorio',   descripcion: 'Comodín misterioso. Buena suerte.',        emoji: '🌀', casilleros_efecto: 0, monto_efecto: 0 },
        { casillero: 18, tipo: 'trampa_pierde_turno', descripcion: 'Suspendido por fecha. No tirás el dado.',  emoji: '🚫', casilleros_efecto: 0, monto_efecto: 0 },
        { casillero: 19, tipo: 'comodin_fijo',        descripcion: 'Sabotaje -$1M al rival que elijas',        emoji: '🗡️', casilleros_efecto: 0, monto_efecto: 0,       comodin_tipo_id: 'sabotaje' },
        { casillero: 20, tipo: 'comodin_fijo',        descripcion: '+$4M ¡Sos el presidente del club!',        emoji: '🏦', casilleros_efecto: 0, monto_efecto: 0,        comodin_tipo_id: 'dinero_extra_plusplus' },
        { casillero: 21, tipo: 'trampa_combo',        descripcion: 'El presidente cortó el presupuesto. Retrocedés 3 y perdés $3M.', emoji: '🔴', casilleros_efecto: 3, monto_efecto: 3000000 },
        { casillero: 22, tipo: 'comodin_fijo',        descripcion: 'Girás de nuevo una restricción tuya',      emoji: '🔄', casilleros_efecto: 0, monto_efecto: 0,        comodin_tipo_id: 'cambiar_restriccion' },
        { casillero: 23, tipo: 'comodin_aleatorio',   descripcion: 'Última oportunidad de robar algo útil',    emoji: '🃏', casilleros_efecto: 0, monto_efecto: 0 },
        { casillero: 24, tipo: 'bonus_avance',        descripcion: '¡Gol en el descuento! Avanzás 3 más.',     emoji: '⚡', casilleros_efecto: 3, monto_efecto: 0 },
        { casillero: 25, tipo: 'comodin_fijo',        descripcion: 'El líder sufre una restricción extra 😈',  emoji: '😈', casilleros_efecto: 0, monto_efecto: 0,        comodin_tipo_id: 'restriccion_extra_lider' },
      ].map(c => ({ ...c, torneo_id: torneo.id }));
      await supabase.from('casillero_config').insert(CASILLEROS_DEFAULT);

      await supabase.from('torneo_participantes').insert({
        torneo_id: torneo.id,
        user_id: session.user.id,
        nombre_usuario: nickNameCrear,
      });

      navigate(`/torneo/${torneo.id}`);
    } catch (e) {
      setError('Error al crear el torneo');
    } finally { setCreando(false); }
  };

  const unirseATorneo = async () => {
    if (!codigoIngreso.trim()) { setError('Ingresá el código'); return; }
    setError('');

    // Validar equipo armado antes de unirse (solo táctico)
    if (tipo === 'tactico') {
      const { data: ventana } = await supabase
        .from('ventana_transferencias').select('numero_fecha').eq('activa', true)
        .order('numero_fecha', { ascending: false }).limit(1).maybeSingle();
      const jornada = ventana?.numero_fecha ?? 1;
      const { data: drafts } = await supabase
        .from('drafts').select('confirmado, equipo')
        .eq('user_id', session.user.id).eq('jornada', jornada);
      const draft = drafts?.[0];
      const cantJugadores = draft?.equipo ? Object.values(draft.equipo).filter(Boolean).length : 0;
      if (!draft?.confirmado || cantJugadores < 11) {
        setError('Tenés que armar y confirmar tu equipo táctico antes de unirte. Andá a Mi Equipo → Táctico.');
        return;
      }
    }

    const { data: torneo } = await supabase
      .from('torneos')
      .select('*')
      .eq('codigo_invitacion', codigoIngreso.trim().toLowerCase())
      .eq('tipo', tipo)
      .single();

    if (!torneo) { setError('Código inválido o torneo no encontrado'); return; }

    const { data: perfil } = await supabase
      .from('perfiles').select('nickname').eq('id', session.user.id).single();
    const nickName = perfil?.nickname || session.user.email.split('@')[0];

    const { error: err } = await supabase.from('torneo_participantes').insert({
      torneo_id: torneo.id,
      user_id: session.user.id,
      nombre_usuario: nickName,
    });

    if (err?.code === '23505') { setError('Ya estás en este torneo'); return; }
    if (err) { setError('Error al unirse'); return; }
    navigate(`/torneo/${torneo.id}`);
  };

  const estiloCard = {
    background: '#1a2233', border: `1px solid #333`,
    borderRadius: '12px', padding: '20px',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    cursor: 'pointer', transition: 'border-color 0.2s',
  };

  return (
    <div style={{ padding: '40px 30px', color: 'white', maxWidth: '860px', margin: '0 auto' }}>
      <h1 style={{ margin: '0 0 4px', fontSize: '1.6rem' }}>
        TORNEO <span style={{ color: tipoColor }}>{tipoLabel.toUpperCase()}</span>
      </h1>
      <p style={{ color: '#aaa', marginBottom: '28px', fontSize: '0.9rem' }}>
        {tipo === 'tactico' ? 'Con restricciones de ruleta' : 'Sin restricciones · $60M'}
      </p>

      {/* BOTONES SIEMPRE VISIBLES */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '20px' }}>
        <button
          onClick={() => { setVista('lista'); setError(''); }}
          style={{ background: vista === 'lista' ? tipoColor : 'transparent', color: vista === 'lista' ? '#000' : tipoColor, border: `1px solid ${tipoColor}`, padding: '8px 18px', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', fontSize: '0.85rem', transition: '0.2s' }}>
          Mis torneos
        </button>
        <button
          onClick={() => { setVista(vista === 'crear' ? 'lista' : 'crear'); setError(''); }}
          style={{ background: vista === 'crear' ? tipoColor : 'transparent', color: vista === 'crear' ? '#000' : tipoColor, border: `1px solid ${tipoColor}`, padding: '8px 18px', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', fontSize: '0.85rem', transition: '0.2s' }}>
          + Crear torneo
        </button>
        <button
          onClick={() => { const newVista = vista === 'buscar' ? 'lista' : 'buscar'; setVista(newVista); setError(''); setBusqueda(''); if (newVista === 'buscar') buscarTorneos(''); }}
          style={{ background: vista === 'buscar' ? tipoColor : 'transparent', color: vista === 'buscar' ? '#000' : tipoColor, border: `1px solid ${tipoColor}`, padding: '8px 18px', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', fontSize: '0.85rem', transition: '0.2s' }}>
          Buscar torneos
        </button>
      </div>

      {/* FORMULARIO INLINE — aparece debajo sin mover nada */}
      {vista !== 'lista' && (
        <div style={{ background: '#0a0f19', borderRadius: '12px', padding: '20px 24px', marginBottom: '24px', border: `1px solid ${tipoColor}44`, display: 'flex', gap: '10px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <input
            value={vista === 'crear' ? nombreNuevo : codigoIngreso}
            onChange={e => vista === 'crear' ? setNombreNuevo(e.target.value) : setCodigoIngreso(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && (vista === 'crear' ? crearTorneo() : unirseATorneo())}
            placeholder={vista === 'crear' ? 'Nombre del torneo' : 'Código de invitación'}
            autoFocus
            style={{ flex: 1, minWidth: '200px', padding: '9px 14px', background: '#1a2233', border: '1px solid #333', borderRadius: '8px', color: '#fff', fontSize: '0.9rem', fontFamily: vista === 'unirse' ? 'monospace' : 'inherit' }}
          />
          {vista === 'crear' && (
            <button onClick={() => setTorneoPublico(v => !v)}
              style={{ padding: '9px 14px', background: torneoPublico ? '#22c55e22' : '#1a2233', color: torneoPublico ? '#22c55e' : '#64748b', border: `1px solid ${torneoPublico ? '#22c55e55' : '#333'}`, borderRadius: '8px', cursor: 'pointer', fontSize: '0.82rem', whiteSpace: 'nowrap', transition: '0.2s' }}>
              {torneoPublico ? ' Público' : ' Privado'}
            </button>
          )}
          <button
            onClick={vista === 'crear' ? crearTorneo : unirseATorneo}
            disabled={creando}
            style={{ padding: '9px 20px', background: tipoColor, color: '#000', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
            {creando ? '...' : vista === 'crear' ? 'Crear' : 'Unirse'}
          </button>
          {error && <p style={{ width: '100%', color: '#ff4444', fontSize: '0.8rem', margin: '4px 0 0' }}>{error}</p>}
        </div>
      )}

      {/* BUSCADOR DE TORNEOS PÚBLICOS */}
      {vista === 'buscar' && (
        <div style={{ background: '#0a0f19', borderRadius: '12px', padding: '20px 24px', marginBottom: '24px', border: `1px solid ${tipoColor}44` }}>
          {/* Unirse con código */}
          <p style={{ color: tipoColor, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '1px', margin: '0 0 8px' }}>Unirse con código</p>
          <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
            <input value={codigo} onChange={e => setCodigo(e.target.value.toUpperCase())}
              placeholder="Ej: DTXI2026" 
              style={{ flex: 1, padding: '9px 14px', background: '#1a2233', border: '1px solid #333', borderRadius: '8px', color: '#fff', fontSize: '0.9rem', fontFamily: 'monospace', letterSpacing: '1px' }}
            />
            <button onClick={unirseATorneo}
              style={{ padding: '9px 18px', background: tipoColor, color: '#000', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
              Unirse
            </button>
          </div>
          {error && <p style={{ color: '#ff4444', fontSize: '0.8rem', margin: '-12px 0 12px' }}>{error}</p>}
          {/* Buscador */}
          <p style={{ color: tipoColor, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '1px', margin: '0 0 8px' }}>Torneos públicos</p>
          <input
            value={busqueda}
            onChange={e => buscarTorneos(e.target.value)}
            placeholder="Filtrar por nombre..."
            style={{ width: '100%', padding: '9px 14px', background: '#1a2233', border: '1px solid #333', borderRadius: '8px', color: '#fff', fontSize: '0.9rem', boxSizing: 'border-box', marginBottom: '14px' }}
          />
          {buscando && <p style={{ color: '#475569', fontSize: '0.8rem', margin: 0 }}>Buscando...</p>}
          {!buscando && busqueda && resultadosBusqueda.length === 0 && (
            <p style={{ color: '#475569', fontSize: '0.8rem', margin: 0 }}>No se encontraron torneos con ese nombre</p>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '10px' }}>
          {resultadosBusqueda.map(t => (
            <div key={t.id} style={{ display: 'flex', flexDirection: 'column', gap: '10px', padding: '16px', background: '#0f172a', borderRadius: '10px', border: '1px solid #1e293b' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ margin: 0, fontWeight: 'bold', fontSize: '1rem', color: '#e2e8f0' }}>{t.nombre}</p>
                <p style={{ margin: '4px 0 0', fontSize: '0.78rem', color: '#475569' }}>
                  por <span style={{ color: '#94a3b8' }}>{t.creador_nick}</span>
                  <span style={{ margin: '0 8px', color: '#334155' }}>·</span>
                  <span style={{ color: tipoColor }}>{t.participantes} miembro{t.participantes !== 1 ? 's' : ''}</span>
                </p>
              </div>
              {t.yaEsta ? (
                <button onClick={() => navigate(`/torneo/${t.id}`)}
                  style={{ width: '100%', padding: '7px', background: 'transparent', color: tipoColor, border: `1px solid ${tipoColor}44`, borderRadius: '6px', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 'bold' }}>
                  Ver torneo →
                </button>
              ) : (
                <button onClick={() => unirseDirecto(t.id)} disabled={uniendose === t.id}
                  style={{ width: '100%', padding: '7px', background: tipoColor, color: '#000', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 'bold', opacity: uniendose === t.id ? 0.6 : 1 }}>
                  {uniendose === t.id ? '...' : 'Unirse'}
                </button>
              )}
            </div>
          ))}
          </div>
        </div>
      )}

      {/* LISTA DE TORNEOS — solo cuando no hay otra sección activa */}
      {vista === 'lista' && (loading ? <Loader texto="Cargando torneo..." /> : (
        torneos.length === 0 ? (
          <div style={{ background: '#0a0f19', borderRadius: '12px', padding: '40px', textAlign: 'center' }}>
            <p style={{ color: '#555', fontSize: '1rem' }}>No estás en ningún torneo todavía</p>
            <p style={{ color: '#444', fontSize: '0.85rem' }}>Creá uno o unite con un código de invitación</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {torneos.map(t => (
              <div key={t.id} style={estiloCard}
                onMouseEnter={e => e.currentTarget.style.borderColor = tipoColor}
                onMouseLeave={e => e.currentTarget.style.borderColor = '#333'}
                onClick={() => navigate(`/torneo/${t.id}`)}>
                <div>
                  <p style={{ margin: 0, fontWeight: 'bold', fontSize: '1rem' }}>
                    {t.clan?.tag && (
                      <span style={{ color: tipoColor, fontSize: '0.75rem', border: `1px solid ${tipoColor}55`, borderRadius: '4px', padding: '1px 6px', marginRight: '7px', letterSpacing: '1px' }}>[{t.clan.tag}]</span>
                    )}
                    {t.nombre}
                  </p>
                  <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: '0.8rem' }}>
                    Código: <span style={{ color: tipoColor, fontFamily: 'monospace' }}>{t.codigo_invitacion}</span>
                    {t.creador_id === session.user.id && <span style={{ marginLeft: '8px', color: '#f59e0b', fontSize: '0.75rem' }}>★ Creador</span>}
                  </p>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {/* Admin puede borrar siempre */}
                  {esAdmin && (
                    <button onClick={(e) => borrarTorneo(e, t.id)}
                      style={{ background: '#ef444422', border: '1px solid #ef444444', borderRadius: '6px', padding: '4px 10px', color: '#ef4444', fontSize: '0.75rem', cursor: 'pointer', fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                      ✕ Borrar
                    </button>
                  )}
                  {/* Creador solo → Borrar. Creador con más gente → Salir (disuelve si queda vacío). No creador → Salir */}
                  {!esAdmin && t.creador_id === session.user.id && t.participantes <= 1 && (
                    <button onClick={(e) => borrarTorneo(e, t.id)}
                      style={{ background: '#ef444422', border: '1px solid #ef444444', borderRadius: '6px', padding: '4px 10px', color: '#ef4444', fontSize: '0.75rem', cursor: 'pointer', fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                      ✕ Borrar
                    </button>
                  )}
                  {!esAdmin && t.creador_id === session.user.id && t.participantes > 1 && (
                    <button onClick={(e) => abandonarTorneo(e, t.id)}
                      style={{ background: '#f59e0b22', border: '1px solid #f59e0b44', borderRadius: '6px', padding: '4px 10px', color: '#f59e0b', fontSize: '0.75rem', cursor: 'pointer', fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                      ← Salir
                    </button>
                  )}
                  {!esAdmin && t.creador_id !== session.user.id && (
                    <button onClick={(e) => abandonarTorneo(e, t.id)}
                      style={{ background: '#f59e0b22', border: '1px solid #f59e0b44', borderRadius: '6px', padding: '4px 10px', color: '#f59e0b', fontSize: '0.75rem', cursor: 'pointer', fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                      ← Salir
                    </button>
                  )}
                  <span style={{ color: tipoColor, fontSize: '1.2rem' }}>›</span>
                </div>
              </div>
            ))}
          </div>
        )
      ))}
    </div>
  );
};

export default TorneoPage;